import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { getAuthHeaders, isAuthenticated } from '../utils/auth';
import './CandidateMatching.css';

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return <div className={`candidate-toast ${type}`}>{message}</div>;
}

const formatDate = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return value;
  }
};

const scoreClass = (score) => {
  const value = Number(score || 0);
  if (value >= 80) return 'high';
  if (value >= 60) return 'medium';
  return 'low';
};

const formatStatus = (value) => {
  if (!value) return '-';
  return String(value)
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const HISTORY_PAGE_SIZE = 5;
const RESULTS_PAGE_SIZE = 8;
const ACCEPTED_CANDIDATE_FILE_TYPES = '.pdf,.docx';

const isAllowedCandidateFile = (file) => /\.(pdf|docx)$/i.test(file?.name || '');

const jobOptionLabel = (job) => {
  const title = job.title || job.positionId || `Job ${job.id}`;
  const company = job.company ? ` - ${job.company}` : '';
  const inactive = job.active === false ? ' (Inactive)' : '';
  return `${title}${company}${inactive}`;
};

const descriptionFromJob = (job) => {
  if (!job) return '';
  if (job.description && job.description.trim()) {
    return job.description.trim();
  }
  const parts = [];
  const addPart = (label, value) => {
    if (value && String(value).trim()) parts.push(`${label}: ${String(value).trim()}`);
  };
  addPart('Company', job.company);
  addPart('Title', job.title);
  addPart('Qualification', job.qualification);
  addPart('Experience', job.experienceRequired);
  addPart('Location', job.location);
  addPart('Job Type', job.jobType);
  addPart('Technology', job.technology);
  return parts.join('\n');
};

export default function CandidateMatching() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [batches, setBatches] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotalElements, setHistoryTotalElements] = useState(0);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [jobDescription, setJobDescription] = useState('');
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedJobDescription, setSelectedJobDescription] = useState('');
  const [shortlistThreshold, setShortlistThreshold] = useState(70);
  const [candidateFiles, setCandidateFiles] = useState([]);
  const [matching, setMatching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [resultPage, setResultPage] = useState(0);
  const [downloadingReportId, setDownloadingReportId] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    if (!isAuthenticated()) {
      const timer = setTimeout(() => {
        if (!isAuthenticated()) navigate('/signup');
        else setAuthChecked(true);
      }, 100);
      return () => clearTimeout(timer);
    }
    setAuthChecked(true);
  }, [navigate]);

  const loadHistoryPage = useCallback(async (pageNumber = 0, showSpinner = false) => {
    if (showSpinner) setHistoryLoading(true);
    const res = await fetch(`${API_ENDPOINTS.CANDIDATE_MATCHES.ADMIN_LIST}?page=${pageNumber}&size=${HISTORY_PAGE_SIZE}`, {
      credentials: 'include',
      headers: getAuthHeaders(),
    });
    if (res.status === 403) throw new Error('Admin access required');
    if (!res.ok) throw new Error('Failed to load candidate match history');

    const data = await res.json();
    setBatches(data.batches || []);
    setHistoryPage(data.currentPage || 0);
    setHistoryTotalPages(data.totalPages || 1);
    setHistoryTotalElements(data.totalElements || 0);
    if (showSpinner) setHistoryLoading(false);
  }, []);

  const loadJobOptions = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.JOBS.LIST}?page=0&size=200`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.status === 403) throw new Error('Sign in required to load jobs');
      if (!res.ok) throw new Error('Failed to load jobs');
      const data = await res.json();
      setJobs(data.jobs || []);
    } catch (e) {
      setToast({ message: e.message || 'Failed to load jobs', type: 'error' });
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const fetchAdminData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadHistoryPage(0), loadJobOptions()]);
    } catch (e) {
      setError(e.message || 'Failed to load candidate matching page');
    } finally {
      setLoading(false);
    }
  }, [loadHistoryPage, loadJobOptions]);

  useEffect(() => {
    if (authChecked) fetchAdminData();
  }, [authChecked, fetchAdminData]);

  const refreshHistory = async (pageNumber = historyPage) => {
    setHistoryLoading(true);
    try {
      await loadHistoryPage(pageNumber);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (candidateFiles.length === 0) {
      showToast('Select candidate PDF or DOCX resume files', 'error');
      return;
    }
    if (candidateFiles.some((file) => !isAllowedCandidateFile(file))) {
      showToast('Only PDF and DOCX resume files are supported', 'error');
      return;
    }
    if (!selectedJobId && !jobDescription.trim()) {
      showToast('Select a job or enter a job description', 'error');
      return;
    }

    setMatching(true);
    setMatchResult(null);
    try {
      const formData = new FormData();
      candidateFiles.forEach((file) => formData.append('file', file));
      if (selectedJobId) {
        formData.append('jobId', selectedJobId);
        if (jobDescription.trim() && jobDescription.trim() !== selectedJobDescription.trim()) {
          formData.append('jobDescription', jobDescription.trim());
        }
      } else {
        formData.append('jobDescription', jobDescription.trim());
      }
      formData.append('shortlistThreshold', String(shortlistThreshold || 70));

      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const res = await fetch(API_ENDPOINTS.CANDIDATE_MATCHES.ADMIN_UPLOAD, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      });

      const data = await res.json().catch(() => ({}));
      if (res.status === 403) throw new Error('Admin access required');
      if (!res.ok) throw new Error(data.message || 'Candidate matching failed');

      setMatchResult(data);
      setResultPage(0);
      showToast(`Analyzed ${data.totalCandidates || 0} candidates. Shortlisted ${data.shortlistedCount || 0}.`);
      setCandidateFiles([]);
      const input = document.getElementById('candidate-match-file');
      if (input) input.value = '';
      await refreshHistory(0);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setMatching(false);
    }
  };

  const loadBatch = async (batchId) => {
    setHistoryLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.CANDIDATE_MATCHES.ADMIN_DETAIL(batchId), {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (res.status === 403) throw new Error('Admin access required');
      if (!res.ok) throw new Error('Failed to load batch details');
      const data = await res.json();
      setMatchResult(data);
      setResultPage(0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleJobSelection = (jobId) => {
    setSelectedJobId(jobId);
    if (!jobId) {
      setSelectedJobDescription('');
      return;
    }
    const selectedJob = jobs.find((job) => String(job.id) === String(jobId));
    const nextDescription = descriptionFromJob(selectedJob);
    setSelectedJobDescription(nextDescription);
    setJobDescription(nextDescription);
  };

  const filenameFromDisposition = (disposition, fallback) => {
    if (!disposition) return fallback;
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
    const asciiMatch = disposition.match(/filename="?([^"]+)"?/i);
    return asciiMatch?.[1] || fallback;
  };

  const handleDownloadReport = async (batchId) => {
    if (!batchId) return;
    setDownloadingReportId(batchId);
    try {
      const headers = getAuthHeaders({
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      delete headers['Content-Type'];

      const res = await fetch(API_ENDPOINTS.CANDIDATE_MATCHES.ADMIN_REPORT(batchId), {
        credentials: 'include',
        headers,
      });
      if (res.status === 403) throw new Error('Admin access required');
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to download candidate report');
      }

      const blob = await res.blob();
      const fallbackName = `candidate-match-report-batch-${batchId}.xlsx`;
      const filename = filenameFromDisposition(res.headers.get('Content-Disposition'), fallbackName);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Candidate report downloaded');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setDownloadingReportId(null);
    }
  };

  if (!authChecked) return null;

  if (loading) {
    return (
      <div className="candidate-match-page">
        <div className="candidate-match-container">
          <div className="candidate-loading">
            <div className="candidate-spinner" />
            <p>Loading candidate matching...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="candidate-match-page">
        <div className="candidate-match-container">
          <div className="candidate-error">
            <strong>{error}</strong>
            <button onClick={fetchAdminData}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const resultRows = matchResult?.results || [];
  const resultTotalPages = Math.max(1, Math.ceil(resultRows.length / RESULTS_PAGE_SIZE));
  const currentResultPage = Math.min(resultPage, resultTotalPages - 1);
  const paginatedResultRows = resultRows.slice(
    currentResultPage * RESULTS_PAGE_SIZE,
    currentResultPage * RESULTS_PAGE_SIZE + RESULTS_PAGE_SIZE
  );

  return (
    <div className="candidate-match-page">
      <div className="candidate-match-container">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        <div className="candidate-match-header">
          <div>
            <h1>AI Candidate Matching</h1>
            <p className="admin-note">Admin View - Bulk Profile Shortlisting</p>
          </div>
          <button className="candidate-secondary-btn" onClick={() => refreshHistory()} disabled={historyLoading}>
            {historyLoading ? 'Refreshing...' : 'Refresh History'}
          </button>
        </div>

        <div className="candidate-match-grid">
          <section className="candidate-match-panel candidate-upload-panel">
            <div className="candidate-panel-title">
              <h2>Run Match Batch</h2>
              <span>Custom JD matching</span>
            </div>

            <div className="candidate-form-grid">
              <div className="candidate-field compact">
                <label>Shortlist Score</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={shortlistThreshold}
                  onChange={(e) => setShortlistThreshold(e.target.value)}
                />
              </div>
            </div>

            <div className="candidate-field">
              <label>Select Job</label>
              <select
                value={selectedJobId}
                onChange={(e) => handleJobSelection(e.target.value)}
                disabled={jobsLoading}
              >
                <option value="">{jobsLoading ? 'Loading jobs...' : 'Custom job description'}</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {jobOptionLabel(job)}
                  </option>
                ))}
              </select>
            </div>

            <div className="candidate-field">
              <label>Job Description</label>
              <textarea
                rows={6}
                value={jobDescription}
                placeholder="Paste the job description and required skills."
                onChange={(e) => setJobDescription(e.target.value)}
              />
            </div>

            <div className="candidate-file-row">
              <input
                id="candidate-match-file"
                type="file"
                accept={ACCEPTED_CANDIDATE_FILE_TYPES}
                multiple
                onChange={(e) => setCandidateFiles(Array.from(e.target.files || []))}
              />
              <button className="candidate-primary-btn" onClick={handleSubmit} disabled={matching}>
                {matching ? 'Analyzing...' : 'Analyze Candidates'}
              </button>
            </div>

            <p className="candidate-help-text">
              Upload one or more PDF or DOCX resume files directly.
            </p>
          </section>

          <section className="candidate-match-panel candidate-history-panel">
            <div className="candidate-panel-title">
              <h2>Recent Batches</h2>
              <span>{historyTotalElements} total</span>
            </div>
            {batches.length === 0 ? (
              <div className="candidate-empty">No candidate match batches yet.</div>
            ) : (
              <div className="candidate-history-list">
                {batches.map((batch) => (
                  <button key={batch.id} className="candidate-history-item" onClick={() => loadBatch(batch.id)}>
                    <span className="history-title-row">
                      <span className="history-title">{batch.jobTitle || `Batch ${batch.id}`}</span>
                      <span className="history-meta">
                        {batch.totalCandidates || 0} candidates / {batch.shortlistedCount || 0} shortlisted
                      </span>
                    </span>
                    <span className="history-bottom-row">
                      <span className={`history-status ${String(batch.status || '').toLowerCase()}`}>{formatStatus(batch.status)}</span>
                      <span className="history-date">{formatDate(batch.createdAt)}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="candidate-history-footer">
              <button
                className="candidate-page-btn"
                disabled={historyPage === 0 || historyLoading}
                onClick={() => refreshHistory(historyPage - 1)}
              >
                Prev
              </button>
              <span>Page {historyPage + 1} of {historyTotalPages}</span>
              <button
                className="candidate-page-btn"
                disabled={historyPage >= historyTotalPages - 1 || historyLoading}
                onClick={() => refreshHistory(historyPage + 1)}
              >
                Next
              </button>
            </div>
          </section>
        </div>

        {matchResult && (
          <>
            <div className="candidate-result-summary">
              <div className="candidate-stat">
                <span>Total</span>
                <strong>{matchResult.totalCandidates || 0}</strong>
              </div>
              <div className="candidate-stat shortlisted">
                <span>Shortlisted</span>
                <strong>{matchResult.shortlistedCount || 0}</strong>
              </div>
              <div className="candidate-stat failed">
                <span>Failed</span>
                <strong>{matchResult.failedCount || 0}</strong>
              </div>
              <div className="candidate-stat">
                <span>Status</span>
                <strong>{matchResult.status || '-'}</strong>
              </div>
            </div>

            <section className="candidate-match-panel candidate-results-panel">
              <div className="candidate-panel-title">
                <div>
                  <h2>{matchResult.jobTitle || 'Match Results'}</h2>
                  <span>Batch #{matchResult.id}</span>
                </div>
                <button
                  className="candidate-report-btn"
                  onClick={() => handleDownloadReport(matchResult.id)}
                  disabled={downloadingReportId === matchResult.id}
                >
                  {downloadingReportId === matchResult.id ? 'Downloading...' : 'Download Report'}
                </button>
              </div>

              {resultRows.length === 0 ? (
                <div className="candidate-empty">No results available for this batch.</div>
              ) : (
                <>
                  <div className="candidate-results-table-wrap">
                    <table className="candidate-results-table">
                      <thead>
                        <tr>
                          <th>Candidate</th>
                          <th>Contact</th>
                          <th>Score</th>
                          <th>Decision</th>
                          <th>Summary</th>
                          <th>Strengths</th>
                          <th>Gaps</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedResultRows.map((result) => (
                          <tr key={result.id} className={result.shortlisted ? 'shortlisted-row' : ''}>
                            <td>
                              <div className="candidate-name">{result.candidateName || `Row ${result.rowNumber || '-'}`}</div>
                              <div className="candidate-row">Row {result.rowNumber || '-'}</div>
                            </td>
                            <td>
                              <div>{result.candidateEmail || '-'}</div>
                              <div className="candidate-row">{result.candidatePhone || ''}</div>
                            </td>
                            <td>
                              <span className={`candidate-score ${scoreClass(result.score)}`}>{Number(result.score || 0).toFixed(0)}</span>
                            </td>
                            <td>
                              <span className={`candidate-decision ${result.shortlisted ? 'yes' : 'no'}`}>
                                {result.shortlisted ? 'Shortlist' : result.recommendation || 'Review'}
                              </span>
                              {result.errorMessage && <div className="candidate-error-text">{result.errorMessage}</div>}
                            </td>
                            <td className="candidate-summary-cell">{result.summary || '-'}</td>
                            <td>{(result.strengths || []).slice(0, 3).join(', ') || '-'}</td>
                            <td>{(result.gaps || []).slice(0, 3).join(', ') || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="candidate-results-footer">
                    <span>
                      Showing {currentResultPage * RESULTS_PAGE_SIZE + 1}-
                      {Math.min((currentResultPage + 1) * RESULTS_PAGE_SIZE, resultRows.length)} of {resultRows.length}
                    </span>
                    <div className="candidate-pagination">
                      <button
                        className="candidate-page-btn"
                        disabled={currentResultPage === 0}
                        onClick={() => setResultPage(currentResultPage - 1)}
                      >
                        Prev
                      </button>
                      <span>Page {currentResultPage + 1} of {resultTotalPages}</span>
                      <button
                        className="candidate-page-btn"
                        disabled={currentResultPage >= resultTotalPages - 1}
                        onClick={() => setResultPage(currentResultPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
